import React from "react";
import { Button } from "../ui/button";
import { ScheduleGrid } from "../ScheduleGrid";
import { VariantCards } from "../VariantCards";
import { MONTHS_HI, MONTHS_EN } from "../../data/rssbData";
import { Sparkles, FileDown, FileSpreadsheet, Loader2, Printer, PencilRuler, Save, Home, UserRound } from "lucide-react";

const PRIORITIES = [
  { v: "balanced", label: "संतुलित / Balanced" },
  { v: "rotation", label: "अधिकतम रोटेशन / Max Rotation" },
];

export const PlannerTab = ({
  monthOptions,
  selMonth,
  setSelMonth,
  variantCount,
  setVariantCount,
  priority,
  setPriority,
  onGenerate,
  onManual,
  generating,
  result,
  selectedId,
  onSelectVariant,
  onReshuffle,
  onCellClick,
  onDayTime,
  onExportPdf,
  onExportExcel,
  onSlips,
  onSave,
  onPrint,
  exportingPdf,
}) => {
  const selectedVariant = result?.variants.find((v) => v.id === selectedId);

  return (
    <div className="space-y-5">
      {/* control bar */}
      <div className="card-3d bg-gradient-to-b from-[#f6eeda] to-[#fffdf7] border border-[#e3d6b6] rounded-xl p-4 flex flex-wrap gap-3 items-end sticky top-[64px] z-30">
        <div>
          <label className="block text-xs text-[#7a4a3a] mb-1">माह / Month</label>
          <select
            data-testid="month-select"
            value={selMonth}
            onChange={(e) => setSelMonth(e.target.value)}
            className="bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm text-[#3a1212] min-w-[160px]"
          >
            {monthOptions.map((m) => (
              <option key={m.key} value={m.key}>
                {MONTHS_HI[m.month]} {m.year} / {MONTHS_EN[m.month]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#7a4a3a] mb-1">विकल्प / Variants</label>
          <select
            data-testid="variant-count"
            value={variantCount}
            onChange={(e) => setVariantCount(Number(e.target.value))}
            className="bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm text-[#3a1212]"
          >
            <option value={3}>3</option>
            <option value={5}>5</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-[#7a4a3a] mb-1">प्राथमिकता / Priority</label>
          <select
            data-testid="priority-select"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="bg-[#f6eeda] border border-[#e3d6b6] rounded-md px-3 py-2 text-sm text-[#3a1212]"
          >
            {PRIORITIES.map((p) => (
              <option key={p.v} value={p.v}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <Button
          data-testid="generate-btn"
          onClick={onGenerate}
          disabled={generating}
          className="bg-gradient-to-b from-[#7a1f1f] to-[#5e1414] border border-[#431010] text-[#f3ead6] font-bold gap-2 btn-3d-red"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          शेड्यूल जनरेट करें
        </Button>
        <Button
          data-testid="manual-btn"
          onClick={onManual}
          variant="outline"
          className="border-[#6e1b1b]/50 text-[#3a1212] hover:bg-[#6e1b1b]/10 gap-2"
        >
          <PencilRuler className="w-4 h-4" /> मैनुअल शीट
        </Button>

        <div className="ml-auto flex flex-wrap gap-2 items-center">
          <Button
            data-testid="save-schedule-btn"
            onClick={onSave}
            disabled={!selectedVariant}
            className="bg-gradient-to-b from-[#2f6b3a] to-[#1f4d28] text-white border border-[#173a1e] gap-2"
          >
            <Save className="w-4 h-4" /> शेड्यूल सहेजें
          </Button>
          {result?.savedAt && (
            <span data-testid="saved-at" className="text-[11px] text-[#2f6b3a] font-dev">
              ✓ सहेजा: {new Date(result.savedAt).toLocaleString("hi-IN", { dateStyle: "short", timeStyle: "short" })}
            </span>
          )}
          <Button
            data-testid="export-pdf-bw-btn"
            onClick={() => onExportPdf("bw")}
            disabled={!selectedVariant || exportingPdf}
            variant="outline"
            className="border-[#6e1b1b]/50 text-[#6e1b1b] hover:bg-[#6e1b1b]/10 gap-2"
          >
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            B&W हिंदी PDF
          </Button>
          <Button
            data-testid="export-pdf-btn"
            onClick={() => onExportPdf("color")}
            disabled={!selectedVariant || exportingPdf}
            variant="outline"
            className="border-[#6e1b1b]/50 text-[#6e1b1b] hover:bg-[#6e1b1b]/10 gap-2"
          >
            {exportingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
            C-हिंदी PDF
          </Button>
          <Button
            data-testid="print-btn"
            onClick={onPrint}
            disabled={!selectedVariant}
            variant="outline"
            className="border-[#cdb98f] text-[#5a2a24] hover:bg-[#efe4ca] gap-2"
          >
            <Printer className="w-4 h-4" /> प्रिंट
          </Button>
          <Button
            data-testid="export-excel-btn"
            onClick={onExportExcel}
            disabled={!selectedVariant}
            variant="outline"
            className="border-[#6e1b1b]/50 text-[#6e1b1b] hover:bg-[#6e1b1b]/10 gap-2"
          >
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </Button>
          <Button
            data-testid="export-ghar-slips-btn"
            onClick={() => onSlips("ghar")}
            disabled={!selectedVariant}
            variant="outline"
            className="border-[#6e1b1b]/50 text-[#6e1b1b] hover:bg-[#6e1b1b]/10 gap-2 font-dev"
          >
            <Home className="w-4 h-4" /> घर स्लिप Excel
          </Button>
          <Button
            data-testid="export-person-slips-btn"
            onClick={() => onSlips("person")}
            disabled={!selectedVariant}
            variant="outline"
            className="border-[#6e1b1b]/50 text-[#6e1b1b] hover:bg-[#6e1b1b]/10 gap-2 font-dev"
          >
            <UserRound className="w-4 h-4" /> सेवादार स्लिप Excel
          </Button>
        </div>
      </div>

      {!result && (
        <div className="text-center py-20 border border-dashed border-[#e3d6b6] rounded-2xl">
          <Sparkles className="w-12 h-12 mx-auto text-[#6e1b1b]/60 mb-3" />
          <p className="font-serif-dev text-lg text-[#3a1212]">माह चुनें और शेड्यूल जनरेट करें</p>
          <p className="text-sm text-[#7a4a3a] mt-1">
            एल्गोरिद्म रोटेशन और लोड बैलेंस के आधार पर कई विकल्प बनाएगा
          </p>
        </div>
      )}

      {result && (
        <>
          {result.priority !== "manual" && (
            <div>
              <h3 className="font-serif-dev text-lg font-bold text-[#3a1212] mb-3">
                शेड्यूल विकल्प — सर्वश्रेष्ठ चुनें
              </h3>
              <VariantCards
                variants={result.variants}
                selectedId={selectedId}
                onSelect={onSelectVariant}
                onReshuffle={onReshuffle}
              />
            </div>
          )}
          {selectedVariant && (
            <div>
              <h3 className="font-serif-dev text-lg font-bold text-[#3a1212] mb-3">
                {result.priority === "manual"
                  ? "मैनुअल शीट — हर सेल पर क्लिक कर प्रचारक/पाठी चुनें या नाम लिखें"
                  : "पूर्ण शेड्यूल — किसी सेल पर क्लिक कर संपादित करें"}
              </h3>
              <ScheduleGrid variant={selectedVariant} dates={result.dates} onCellClick={onCellClick} onDayTime={onDayTime} />
            </div>
          )}
        </>
      )}
    </div>
  );
};
